import {
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { CloseCircleOutline, CountryCode, useToast } from '@aslanpay/portal-common-lib';

import useCreatePartner from 'app/hooks/useCreatePartner';

import CreatePartnerForm, { PartnerFormFields } from '../CreatePartnerForm';

import { CreatePartnerModalProps } from './types';

function CreatePartnerModal({ isOpen, onClose }: CreatePartnerModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { mutate: createPartner, isLoading } = useCreatePartner({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Partner created',
        description: 'The partner was successfully created.',
      });
      queryClient.invalidateQueries({ queryKey: ['fetchPartnersPage'] });
      onClose();
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, the partner could not be created. Please try again.',
      });
    },
  });

  const handleSubmit = (data: PartnerFormFields) => {
    const {
      addressLine1,
      addressLine2,
      addressLine3,
      city,
      postCode,
      addressCountryCode,
      countryCode,
      ...rest
    } = data;

    const hasAddress =
      addressLine1 || addressLine2 || addressLine3 || city || postCode || addressCountryCode;

    createPartner({
      ...rest,
      countryCode: (countryCode as CountryCode) || undefined,
      address: hasAddress
        ? {
            addressLine1: addressLine1 || undefined,
            addressLine2: addressLine2 || undefined,
            addressLine3: addressLine3 || undefined,
            city: city || undefined,
            postCode: postCode || undefined,
            countryCode: (addressCountryCode as CountryCode) || undefined,
          }
        : undefined,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">Create a new partner</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close create partner"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody pb="24px">
          <CreatePartnerForm
            id="create-partner-form"
            onSubmit={handleSubmit}
            isLoading={isLoading}
            onClose={onClose}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default CreatePartnerModal;
